/**
 * Confirm dialog shown before Distort / Perspective / Warp can run
 * on a non-image layer (vector / shape / text). The underlying
 * rasterizers (`commitNonAffineDeformation` → `setElement`) only
 * work on `fabric.FabricImage`, so we offer to rasterize the
 * layer first. Destructive — the layer becomes pixels and loses
 * vector/shape/text-specific editing. Mirrors Photoshop's smart-
 * object rasterize prompt.
 *
 * The wording on the action button uses the same noun the
 * FreeTransformPanel chip used so the user can connect cause and
 * consequence at a glance.
 */

import type { LayerType } from '../types';

interface RasterizeBeforeWarpDialogProps {
  /** Display name of the layer the user is trying to warp. Shown
   *  in the title so the dialog is unambiguous when multiple layers
   *  are open. */
  layerName: string;
  /** Source `LayerType` of the target. Drives wording — text reads
   *  "rasterize text" rather than "rasterize layer" to make the
   *  consequence concrete. */
  sourceType: LayerType;
  /** Which non-affine mode the user triggered — title says
   *  "before Distort" / "before Perspective" / "before Warp" so
   *  the action label matches what the user just clicked. */
  mode: 'distort' | 'perspective' | 'warp';
  onCancel: () => void;
  /** Confirm: rasterize the layer to a paint surface AND enter the
   *  requested mode. Parent runs both ops atomically. */
  onConfirm: () => void;
}

const MODE_LABEL: Record<RasterizeBeforeWarpDialogProps['mode'], string> = {
  distort: 'Distort',
  perspective: 'Perspective',
  warp: 'Warp',
};

const SOURCE_NOUN: Partial<Record<LayerType, string>> = {
  vector: 'vector path',
  shape: 'shape',
  text: 'text',
};

export function RasterizeBeforeWarpDialog({
  layerName,
  sourceType,
  mode,
  onCancel,
  onConfirm,
}: RasterizeBeforeWarpDialogProps) {
  const BTN_BASE =
    'px-4 py-2 text-sm rounded-md border border-border text-foreground hover:bg-accent';
  const noun = SOURCE_NOUN[sourceType] ?? 'layer';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-semibold">
          Rasterize &ldquo;{layerName}&rdquo; before {MODE_LABEL[mode]}?
        </h2>
        <p className="text-sm text-muted-foreground">
          {MODE_LABEL[mode]} works on pixel layers. Continuing converts this {noun} to pixels — you
          will no longer be able to edit it as a {noun}.
        </p>
        <div className="flex justify-end gap-2 pt-2 flex-wrap">
          <button type="button" className={BTN_BASE} onClick={() => onCancel()}>
            Cancel
          </button>
          <button
            type="button"
            className={`${BTN_BASE} bg-primary text-primary-foreground border-primary hover:bg-primary/90`}
            onClick={() => onConfirm()}
          >
            Rasterize &amp; {MODE_LABEL[mode]}
          </button>
        </div>
      </div>
    </div>
  );
}
